import { Avatar, Stack, Tooltip, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { IMAGE_URL } from "config";
import { useChurchStore } from "features/churches";
import { useDenominationStore } from "features/denominations";
import { FILTER_OPERATIONS } from "features/members";
import moment from "moment";
import queryString from "query-string";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";
import { UAParser } from "ua-parser-js";
import { useSystemLogsPaginated } from "../api";

export const UsersLogTable = ({
  searchQuery,
  selectedUser,
  startDate,
  endDate
}) => {
  const { t } = useTranslation();
  const location = useLocation();
  const queryParams = queryString.parse(location.search);
  const [page, setPage] = useState(Number(queryParams.page) || 1);
  const [pageSize, setPageSize] = useState(Number(queryParams.pageSize) || 10);
  const selectedChurch = useChurchStore(state => state.selectedChurch);
  const selectedDenomination = useDenominationStore(
    state => state.selectedDenomination
  );
  const navigate = useNavigate();

  useEffect(() => {
    const query = queryString.stringify({ page, pageSize });
    navigate(`?t=logs&${query}`, { replace: true });
  }, [page, pageSize, navigate]);

  const { data, isLoading } = useSystemLogsPaginated({
    churchId: selectedChurch?.id,
    page: page,
    pageSize: pageSize,
    isDenomination: window.location.pathname.includes("denomination"),
    denominationId: selectedDenomination?.id,
    queries: {
      search: searchQuery,
      startDate: startDate,
      endDate: endDate,
      filter: selectedUser
        ? {
            user_id: [
              {
                operator: FILTER_OPERATIONS.EQUAL,
                value: selectedUser
              }
            ]
          }
        : null
    }
  });

  const columns = [
    {
      field: "user",
      headerName: t("systemAdmins.logs.User"),
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <Stack direction="row" spacing={2} alignItems="center">
            <Avatar
              src={`${IMAGE_URL}/${row.image_url}`}
              alt={row?.user?.first_name}
              sx={{ outline: "2px solid", outlineColor: "primary.main" }}
            />
            <Typography>
              {row?.user?.first_name} {row?.user?.last_name}
            </Typography>
          </Stack>
        );
      }
    },
    {
      field: "date_and_time",
      headerName: t("systemAdmins.logs.DateAndTime"),
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <span>{moment(row?.created_at).format("YYYY-MM-DD hh:mm:ss A")}</span>
        );
      }
    },
    {
      field: "event",
      headerName: t("systemAdmins.logs.Event"),
      flex: 1
    },
    {
      field: "description",
      headerName: t("systemAdmins.logs.Details"),
      flex: 2,
      renderCell: ({ row }) => {
        return (
          <Tooltip title={row?.description}>
            <Typography
              variant="body2"
              sx={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap"
              }}
            >
              {row?.description}
            </Typography>
          </Tooltip>
        );
      }
    },
    {
      field: "platform",
      headerName: "Platform",
      flex: 1,
      sortable: false,
      renderCell: ({ row }) => {
        const parser = UAParser(row.user_agent);
        const deviceName =
          parser.device.model || parser.device.vendor || "Desktop";

        return (
          <Stack spacing={0.5}>
            <Typography variant="body2">{parser.browser.name} </Typography>
            <Typography variant="caption" color="text.secondary">
              {deviceName} • {parser.os.name}
            </Typography>
          </Stack>
        );
      }
    }
  ];

  return (
    <DataGrid
      autoHeight
      rows={data?.result?.data || []}
      rowCount={data?.result?.total || 0}
      loading={isLoading}
      columns={columns}
      pagination
      rowsPerPageOptions={[5, 10, 15, 25, 50, 100]}
      density="comfortable"
      getRowId={row => row.id}
      disableColumnMenu
      disableSelectionOnClick
      paginationMode="server"
      page={page - 1}
      pageSize={pageSize}
      onPageChange={newPage => setPage(newPage + 1)}
      onPageSizeChange={newPageSize => setPageSize(newPageSize)}
      localeText={{
        noRowsLabel: t("teams.teamProfile.NoRows"),
        MuiTablePagination: {
          labelRowsPerPage: t("teams.teamProfile.RowsPerPage")
        }
      }}
    />
  );
};
